import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, StatusBadge, NativeSelect, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserDetailDialog } from "@/components/UserDetailDialog";

const ROLE_OPTS = [["system_admin", "System Administrator"], ["accounting_officer", "Accounting Officer"],
  ["accounting_supervisor", "Accounting Supervisor"], ["customer_service", "Customer Service"], ["cashier", "Cashier"], ["consumer", "Consumer"]]
  .map(([value, label]) => ({ value, label }));

function ResetDialog({ target, onClose, onDone, endpoint, approve }) {
  const [reason, setReason] = useState("");
  const [idv, setIdv] = useState("");
  const submit = async (decision) => {
    try {
      const body = approve ? { decision, identity_verification: idv, notes: reason } : { reason, identity_verification: idv };
      await api.post(endpoint, body);
      toast.success("Recorded"); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reset-2fa-dialog">
        <DialogHeader><DialogTitle>{approve ? "Decide recovery request" : "Reset Two-Factor Authentication"}</DialogTitle>
          <DialogDescription>{target?.email}. This action is audited and the user is notified. Their sessions are revoked.</DialogDescription></DialogHeader>
        <Textarea placeholder="How was identity verified? (e.g. valid ID presented at Naga office, ID no. …)" value={idv} onChange={(e) => setIdv(e.target.value)} data-testid="reset-identity-input" />
        <Textarea placeholder={approve ? "Notes (optional)" : "Reason for reset"} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reset-reason-input" />
        <DialogFooter className="gap-2">
          {approve ? <>
            <Button variant="outline" disabled={idv.length < 10} onClick={() => submit("reject")} data-testid="recovery-reject-button">Reject</Button>
            <Button disabled={idv.length < 10} onClick={() => submit("approve")} data-testid="recovery-approve-button">Approve & reset 2FA</Button>
          </> : <Button disabled={idv.length < 10 || reason.length < 10} onClick={() => submit()} data-testid="reset-2fa-submit">Reset 2FA</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function UsersPage() {
  const { can, user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [nu, setNu] = useState({ email: "", name: "", role: "customer_service", password: "" });
  const [resetT, setResetT] = useState(null);
  const [detail, setDetail] = useState(null);
  const roleOpts = ROLE_OPTS.filter((o) => o.value !== "system_admin" || me.role === "super_admin");
  const load = () => api.get("/admin/users", { params: { q, page, page_size: 25 } }).then((r) => { setUsers(r.data.items); setTotal(r.data.total); });
  useEffect(() => { load(); }, [q, page]); // eslint-disable-line
  const exportCsv = async () => {
    const { data } = await api.get("/admin/users/export.csv", { params: { q }, responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = "users.csv"; a.click();
  };
  const editable = (u) => u.id !== me.id && (u.role !== "system_admin" || me.role === "super_admin");
  const patch = async (u, body) => { try { await api.patch(`/admin/users/${u.id}`, body); toast.success("User updated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const create = async () => { try { await api.post("/admin/users", nu); setCreating(false); setNu({ email: "", name: "", role: "customer_service", password: "" }); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <PageHeader eyebrow="Administration" title="Users" description={`${total} user(s)`} actions={<><Button variant="outline" onClick={exportCsv} data-testid="export-users-button">Export CSV</Button>{can("users.manage") && <Button onClick={() => setCreating(true)} data-testid="create-user-button">New user</Button>}</>} />
      <Input placeholder="Search name or email" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="max-w-sm mb-4" data-testid="user-search-input" />
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="text-left p-3">User</th><th className="text-left">Role</th><th>2FA</th><th>Status</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody>{users.map((u) => (
            <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
              <td className="p-3 cursor-pointer" onClick={() => setDetail(u)} data-testid={`user-open-${u.email}`}><p className="font-medium text-blue-800 underline">{u.name}</p><p className="text-xs text-slate-500">{u.email}</p></td>
              <td className="w-52 min-w-[11rem]">{can("users.manage") && editable(u) ? <NativeSelect value={u.role} options={roleOpts.some((o) => o.value === u.role) ? roleOpts : ROLE_OPTS} onChange={(v) => patch(u, { role: v })} testId={`user-role-${u.email}`} /> : u.role_label}</td>
              <td className="text-center"><StatusBadge status={u.twofa.enabled ? "enabled" : "disabled"}>{u.twofa.enabled ? [u.twofa.email_enrolled && "Email", u.twofa.totp_enrolled && "App"].filter(Boolean).join(" + ") : "Off"}</StatusBadge>
                {u.twofa.required_by_policy && <p className="text-[10px] text-amber-700 mt-1">required</p>}</td>
              <td className="text-center"><StatusBadge status={u.status} /></td>
              <td className="p-3 text-right space-x-2 whitespace-nowrap">
                {can("recovery.manage") && u.twofa.enabled && u.id !== me.id && <Button size="sm" variant="outline" onClick={() => setResetT(u)} data-testid={`reset-2fa-${u.email}`}>Reset 2FA</Button>}
                {can("users.manage") && editable(u) && <Button size="sm" variant="ghost" onClick={() => patch(u, { status: u.status === "active" ? "suspended" : "active" })} data-testid={`toggle-status-${u.email}`}>{u.status === "active" ? "Suspend" : "Activate"}</Button>}
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <div className="flex justify-end gap-2 mt-3 text-sm items-center">
        <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)} data-testid="users-prev">Previous</Button>
        <span>Page {page} of {Math.max(1, Math.ceil(total / 25))}</span>
        <Button size="sm" variant="outline" disabled={page * 25 >= total} onClick={() => setPage(page + 1)} data-testid="users-next">Next</Button>
      </div>
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader><DialogTitle>New user</DialogTitle><DialogDescription>The default 2FA policy for the selected role applies at first sign-in.</DialogDescription></DialogHeader>
          <Input placeholder="Full name" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} data-testid="new-user-name" />
          <Input placeholder="Email" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} data-testid="new-user-email" />
          <NativeSelect value={nu.role} options={roleOpts} onChange={(v) => setNu({ ...nu, role: v })} testId="new-user-role" />
          <Input type="password" placeholder="Temporary password (min 8)" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} data-testid="new-user-password" />
          <DialogFooter><Button onClick={create} data-testid="new-user-submit">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <ResetDialog target={resetT} onClose={() => setResetT(null)} onDone={() => { setResetT(null); load(); }} endpoint={resetT ? `/admin/users/${resetT.id}/2fa/reset` : ""} />
      {detail && <UserDetailDialog userId={detail.id} canReset={can("users.manage") && editable(detail)} onClose={() => setDetail(null)} />}
    </div>
  );
}

export function RecoveryRequests() {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/admin/recovery-requests").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div>
      <PageHeader eyebrow="Security" title="Account recovery" description="Every reset requires recorded identity verification, an authorized role and recent re-authentication." />
      {items.length === 0 ? <Empty>No recovery records.</Empty> : (
        <div className="grid gap-3">{items.map((r) => (
          <Panel key={r.id} testId={`recovery-${r.id}`}>
            <div className="flex flex-col sm:flex-row justify-between gap-3">
              <div className="text-sm space-y-1">
                <p className="font-medium">{r.full_name || r.email} <span className="text-xs text-slate-500">· {r.email} · {r.type.replace("_", " ")}</span></p>
                <p className="text-slate-600">{r.reason}</p>
                <p className="text-xs text-slate-500">{fmtDate(r.created_at)}{r.account_number ? ` · Acct ${r.account_number}` : ""}{r.user_id ? "" : " · no matching account"}</p>
                {r.identity_verification && <p className="text-xs">Identity verification: {r.identity_verification}</p>}
              </div>
              <div className="flex items-start gap-2"><StatusBadge status={r.status} />
                {r.status === "pending" && <Button size="sm" onClick={() => setSel(r)} data-testid={`decide-recovery-${r.id}`}>Review</Button>}</div>
            </div>
          </Panel>
        ))}</div>
      )}
      <ResetDialog approve target={sel} onClose={() => setSel(null)} onDone={() => { setSel(null); load(); }} endpoint={sel ? `/admin/recovery-requests/${sel.id}/decision` : ""} />
    </div>
  );
}

export function AuditLog() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ action: "", actor: "", target_id: "" });
  useEffect(() => { api.get("/admin/audit-logs", { params: f }).then((r) => setItems(r.data)); }, [f]);
  return (
    <div>
      <PageHeader eyebrow="Compliance" title="Audit log" description="Passwords, OTP values and TOTP secrets are never recorded." />
      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <Input placeholder="Action prefix (e.g. 2fa, twa, registration)" value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} data-testid="audit-action-filter" />
        <Input placeholder="Actor email" value={f.actor} onChange={(e) => setF({ ...f, actor: e.target.value })} data-testid="audit-actor-filter" />
        <Input placeholder="Record ID" value={f.target_id} onChange={(e) => setF({ ...f, target_id: e.target.value })} data-testid="audit-target-filter" />
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-xs" data-testid="audit-table">
          <thead className="bg-slate-50 uppercase text-slate-500"><tr><th className="text-left p-3">Time</th><th className="text-left">Action</th><th className="text-left">Actor</th><th className="text-left">Record</th><th className="text-left p-3">Changes</th></tr></thead>
          <tbody>{items.map((a) => (
            <tr key={a.id} className="border-t border-slate-100 align-top">
              <td className="p-3 whitespace-nowrap">{fmtDate(a.timestamp)}</td><td className="font-mono text-amber-800">{a.action}</td>
              <td>{a.actor_email || "system/anonymous"}</td><td className="font-mono">{a.target_type}<br />{a.target_id?.slice(0, 8)}</td>
              <td className="p-3 font-mono max-w-md break-all text-slate-600">{JSON.stringify(a.changes)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
